/**
 * Centralized Glossary for Climate Risk Metrics
 * 
 * This file serves as the single source of truth for all metric definitions,
 * tooltips, and explanations used across the application.
 * 
 * Usage:
 *   import { GLOSSARY, getGlossaryEntry } from '@/lib/glossary'
 *   <InfoTooltip term="overall_score" />
 */

export interface GlossaryEntry {
    /** Short label for the metric */
    label: string
    /** Brief description (1-2 sentences) for tooltip display */
    description: string
    /** Detailed explanation (optional, for expanded views) */
    details?: string
    /** Unit of measurement (optional) */
    unit?: string
    /** Data range (optional) */
    range?: string
    /** Category for grouping */
    category: 'score' | 'metric' | 'return_period' | 'precipitation' | 'scenario' | 'general'
}

/**
 * GLOSSARY: Central dictionary of all climate risk metrics and terms
 * 
 * Keys should be snake_case and match field names from the API where applicable.
 * This allows easy lookup based on data field names.
 */
export const GLOSSARY: Record<string, GlossaryEntry> = {
    // ============================================
    // RISK SCORES
    // ============================================
    overall_score: {
        label: 'Overall Score',
        description: 'Combined risk score factoring in both hazard intensity and projected climate change impact.',
        details: 'This score synthesizes the hazard score (current exposure) and change score (future climate trend) to provide a holistic risk assessment. Higher values indicate greater risk.',
        range: '0-100',
        category: 'score',
    },
    hazard_score: {
        label: 'Hazard Score',
        description: 'Measures current exposure to climate hazards based on historical and baseline data.',
        details: 'Derived from hazard-specific analysis (e.g., return periods for precipitation, WBGT/exceedance days for heat). Reflects the intensity and frequency of hazards at this location.',
        range: '0-100',
        category: 'score',
    },
    change_score: {
        label: 'Change Score',
        description: 'Quantifies how much the hazard is projected to change under climate scenarios.',
        details: 'Calculated by comparing projected future metrics against the historical baseline. Positive values indicate increasing risk over time.',
        range: '0-100',
        category: 'score',
    },

    // ============================================
    // RETURN PERIODS
    // ============================================
    return_period_generic: {
        label: 'Return Period',
        description: 'The average expected time interval (years) between extreme events of this intensity.',
        details: 'For example, a 100-year return period event has a 1% probability of occurring in any given year. Higher return periods correspond to more extreme and rare events.',
        unit: 'years',
        category: 'return_period',
    },
    return_period_10yr: {
        label: '10-Year Return Period',
        description: 'Maximum 1-day precipitation (mm) expected to occur once every 10 years on average.',
        details: 'Represents relatively frequent extreme events. Used for standard infrastructure planning.',
        unit: 'mm/day',
        category: 'return_period',
    },
    return_period_20yr: {
        label: '20-Year Return Period',
        description: 'Maximum 1-day precipitation (mm) expected to occur once every 20 years on average.',
        unit: 'mm/day',
        category: 'return_period',
    },
    return_period_50yr: {
        label: '50-Year Return Period',
        description: 'Maximum 1-day precipitation (mm) expected to occur once every 50 years on average.',
        details: 'Commonly used for critical infrastructure and flood protection design.',
        unit: 'mm/day',
        category: 'return_period',
    },
    return_period_100yr: {
        label: '100-Year Return Period',
        description: 'Maximum 1-day precipitation (mm) expected to occur once every 100 years on average.',
        details: 'Standard reference for major flood risk and insurance assessments.',
        unit: 'mm/day',
        category: 'return_period',
    },
    return_period_200yr: {
        label: '200-Year Return Period',
        description: 'Maximum 1-day precipitation (mm) expected to occur once every 200 years on average.',
        unit: 'mm/day',
        category: 'return_period',
    },
    return_period_500yr: {
        label: '500-Year Return Period',
        description: 'Maximum 1-day precipitation (mm) expected to occur once every 500 years on average.',
        details: 'Represents extreme tail-risk events. Critical for disaster preparedness planning.',
        unit: 'mm/day',
        category: 'return_period',
    },

    // ============================================
    // PRECIPITATION METRICS
    // ============================================
    precip_annual: {
        label: 'Annual Precipitation',
        description: 'Total accumulated precipitation over a full year.',
        unit: 'mm/year',
        category: 'precipitation',
    },
    precip_monthly: {
        label: 'Monthly Precipitation',
        description: 'Average precipitation for each calendar month (Jan-Dec).',
        details: 'Helps identify seasonal patterns, monsoon periods, and dry seasons.',
        unit: 'mm/month',
        category: 'precipitation',
    },

    // ============================================
    // SSP SCENARIOS
    // ============================================
    ssp126: {
        label: 'SSP1-2.6 (Low Emissions)',
        description: 'Sustainable development pathway with strong climate mitigation. Global warming limited to ~1.8°C by 2100.',
        details: 'Represents the most optimistic scenario with rapid decarbonization and green growth.',
        category: 'scenario',
    },
    ssp245: {
        label: 'SSP2-4.5 (Moderate Emissions)',
        description: 'Middle-of-the-road scenario with moderate mitigation efforts. Global warming of ~2.7°C by 2100.',
        details: 'Assumes gradual transition toward sustainability, often used as a reference baseline.',
        category: 'scenario',
    },
    ssp585: {
        label: 'SSP5-8.5 (High Emissions)',
        description: 'Fossil fuel-intensive development with minimal mitigation. Global warming of ~4.4°C by 2100.',
        details: 'Represents a "worst case" scenario useful for stress-testing resilience strategies.',
        category: 'scenario',
    },

    // ============================================
    // TIME HORIZONS
    // ============================================
    baseline_year: {
        label: 'Baseline Year',
        description: 'Reference year for historical/current climate conditions, typically 2020.',
        category: 'general',
    },
    projection_year: {
        label: 'Projection Year',
        description: 'Future year for climate projections (e.g., 2030, 2050, 2100).',
        category: 'general',
    },

    // ============================================
    // RISK LEVELS
    // ============================================
    risk_very_low: {
        label: 'Very Low Risk',
        description: 'Minimal exposure to climate hazards. Score range: 0-20.',
        category: 'general',
    },
    risk_low: {
        label: 'Low Risk',
        description: 'Limited exposure with manageable impacts. Score range: 21-40.',
        category: 'general',
    },
    risk_moderate: {
        label: 'Moderate Risk',
        description: 'Notable exposure requiring attention. Score range: 41-60.',
        category: 'general',
    },
    risk_high: {
        label: 'High Risk',
        description: 'Significant exposure with potential for serious impacts. Score range: 61-80.',
        category: 'general',
    },
    risk_very_high: {
        label: 'Very High Risk',
        description: 'Critical exposure requiring urgent action. Score range: 81-100.',
        category: 'general',
    },

    // ============================================
    // CHART & VISUALIZATION TERMS
    // ============================================
    hazard_over_time: {
        label: 'Hazard Values Over Time',
        description: 'Chart showing projected changes in the selected metric from 2020 to 2100.',
        details: 'Mean values shown with optional 5th/95th percentile bands for uncertainty visualization.',
        category: 'general',
    },
    risk_distribution: {
        label: 'Risk Distribution',
        description: 'Breakdown of portfolio assets by risk level category.',
        details: 'Shows percentage and count of locations in each risk tier (Very Low to Very High).',
        category: 'general',
    },

    // ============================================
    // PORTFOLIO TERMS
    // ============================================
    portfolio_value: {
        label: 'Portfolio Value',
        description: 'Total insured or asset value across all locations in the portfolio.',
        unit: 'Currency (e.g., USD)',
        category: 'general',
    },
    asset_count: {
        label: 'Asset Count',
        description: 'Number of physical locations or assets in the portfolio.',
        category: 'general',
    },

    // ============================================
    // WIND METRICS
    // ============================================
    wind_speed: {
        label: 'Wind Speed',
        description: 'Sustained wind speed measured at 10m height above ground.',
        details: 'Derived from ERA5 reanalysis and CMIP6 projections. Includes both synoptic weather patterns and tropical cyclone components.',
        unit: 'm/s',
        category: 'metric',
    },
    wind_return_level: {
        label: 'Wind Return Level',
        description: 'Extreme wind speed expected to occur once within the specified return period.',
        details: 'Estimated using Extreme Value Theory (GEV/Gumbel distributions). At each return period, the dominant wind driver — tropical cyclone or synoptic weather — is flagged.',
        unit: 'm/s',
        category: 'return_period',
    },
    tc_dominates: {
        label: 'Tropical Cyclone Dominance',
        description: 'Indicates whether the extreme wind at this return period is driven by tropical cyclones (typhoons) or synoptic (normal weather) winds.',
        details: 'TC=1 means tropical cyclones dominate the extreme wind tail for that return period; TC=0 means synoptic weather patterns are the primary driver.',
        category: 'metric',
    },
}

/**
 * Helper function to get a glossary entry by key
 * Returns undefined if not found
 */
export function getGlossaryEntry(key: string): GlossaryEntry | undefined {
    return GLOSSARY[key]
}

/**
 * Get just the description for a term (for simple tooltip use)
 */
export function getGlossaryDescription(key: string): string {
    return GLOSSARY[key]?.description ?? 'No description available.'
}

/**
 * Get all entries for a category
 */
export function getGlossaryByCategory(category: GlossaryEntry['category']): Record<string, GlossaryEntry> {
    return Object.fromEntries(
        Object.entries(GLOSSARY).filter(([, entry]) => entry.category === category)
    )
}
